import { osnovaApi } from '../base';
import type {
  AssistantAskResponse,
  AssistantFeedbackReport,
  AssistantFeedbackValue,
  AssistantMessage,
  AssistantSession,
} from '@entities/assistant/types';

/** ОСНОВА.Помощник: свои чаты сотрудника и вопросы (`/api/v1/assistant/*`). */
export const assistantApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Свои чаты, недавно активные сверху. */
    listAssistantSessions: b.query<{ items: AssistantSession[] }, void>({
      query: () => '/assistant/sessions',
      providesTags: ['AssistantSession'],
    }),

    /** Сообщения своего чата по порядку. */
    listAssistantMessages: b.query<{ items: AssistantMessage[] }, string>({
      query: (sessionId) => `/assistant/sessions/${sessionId}/messages`,
      providesTags: (_r, _e, sessionId) => [{ type: 'AssistantMessages', id: sessionId }],
    }),

    /** Задать вопрос. Без `session_id` сервер создаёт новый чат. Вопрос
     *  сохраняется до ответа модели, поэтому сообщения чата перечитываем и при
     *  ошибке. */
    askAssistant: b.mutation<
      AssistantAskResponse,
      { question: string; session_id?: string; page?: string }
    >({
      query: (body) => ({ url: '/assistant/ask', method: 'POST', body }),
      invalidatesTags: (res, _err, arg) => {
        const id = res?.session.id ?? arg.session_id;
        return id
          ? ['AssistantSession', { type: 'AssistantMessages', id }]
          : ['AssistantSession'];
      },
    }),

    renameAssistantSession: b.mutation<{ ok: boolean }, { sessionId: string; title: string }>({
      query: ({ sessionId, title }) => ({
        url: `/assistant/sessions/${sessionId}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: ['AssistantSession'],
    }),

    deleteAssistantSession: b.mutation<{ ok: boolean }, string>({
      query: (sessionId) => ({ url: `/assistant/sessions/${sessionId}`, method: 'DELETE' }),
      invalidatesTags: ['AssistantSession'],
    }),

    /** Оценить свой ответ: 1 — полезно, -1 — не полезно, null — снять оценку. */
    rateAssistantAnswer: b.mutation<
      { message: AssistantMessage },
      {
        messageId: string;
        sessionId: string;
        value: AssistantFeedbackValue | null;
        comment?: string;
      }
    >({
      query: ({ messageId, value, comment }) => ({
        url: `/assistant/messages/${messageId}/feedback`,
        method: 'PUT',
        body: { value, comment: comment ?? null },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'AssistantMessages', id: arg.sessionId },
        'AssistantFeedback',
      ],
    }),

    /** Оценки ответов за `days` дней — только администратору. */
    getAssistantFeedback: b.query<{ report: AssistantFeedbackReport }, number>({
      query: (days) => `/admin/assistant/feedback?days=${days}`,
      providesTags: ['AssistantFeedback'],
    }),
  }),
});

export const {
  useListAssistantSessionsQuery,
  useListAssistantMessagesQuery,
  useAskAssistantMutation,
  useRenameAssistantSessionMutation,
  useDeleteAssistantSessionMutation,
  useRateAssistantAnswerMutation,
  useGetAssistantFeedbackQuery,
} = assistantApi;
